/**
 * The schedule on an invoice: what it is, what it has produced, and the two
 * buttons that change it.
 *
 * The card is drawn even when there is no schedule, and that is the whole
 * design decision here. A recurrence that can only be seen once it exists is a
 * feature nobody discovers - there would be nothing on the invoice page saying
 * that a maintenance contract COULD bill itself. So the empty state is the
 * offer, and the filled state is the report.
 *
 * What the filled state has to answer, in the order an operator asks it:
 * when is the next one, how many have there been, and when did the last one
 * actually happen. The third is the one that matters when something has gone
 * wrong: "next: 15 September" on its own cannot distinguish a healthy series
 * from one whose machine stopped running in June.
 */
import { useCallback, useEffect, useState, type CSSProperties, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { CalendarSync, Check, Loader2, Play, Repeat, Send, X } from "lucide-react";
import { DatePicker } from "@wa/ui";
import { useI18n, useT } from "../../../i18n/I18nProvider";
import { ApiError } from "../../../lib/api";
import { formatCents } from "../../../lib/sales";
import {
  getRecurringChildren,
  runInvoiceRecurringNow,
  setInvoiceRecurring,
  toScheduleIso,
  type RecurringChild,
  type RecurringFields,
} from "../../../lib/salesRecurring";
import { Btn, Card, CardHead, CheckField, EmptyState, Modal, fmtDate, useToast } from "../../ui";
import { toDateInput } from "../projects/lib";
import { StatusBadge } from "./shared";

const COLS = "minmax(0,1.5fr) 1fr minmax(0,1fr)";

/** The periods offered. Anything else is a number the operator can still type. */
const PERIODS = [1, 3, 6, 12];

/**
 * What the card needs off the invoice. Not the whole `Invoice`, so this file
 * does not have to be revisited every time a column is added to the document.
 */
export interface RecurringHost extends RecurringFields {
  id: string;
  issueDate: string;
  currency: string;
}

export function RecurringCard({
  invoice,
  canManage,
  onChange,
}: {
  invoice: RecurringHost;
  canManage: boolean;
  onChange: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [children, setChildren] = useState<RecurringChild[]>([]);

  const on = invoice.recurringEveryMonths != null;
  // A generated invoice cannot become a template. The API refuses it; the card
  // does not offer it, so the refusal is never something anybody has to hit.
  const isChild = invoice.recurringParentId != null;
  // A schedule that exists but points nowhere: the series ran to its end date or
  // its count. Worth saying out loud - the alternative is a card that looks
  // switched on and quietly never bills again.
  const finished = on && invoice.recurringNextAt == null;

  const loadChildren = useCallback(async () => {
    if (!on) return;
    try {
      setChildren(await getRecurringChildren(invoice.id));
    } catch {
      // A failed child list is not worth a toast: the schedule above it is the
      // point of the card and it is already on screen.
      setChildren([]);
    }
  }, [invoice.id, on]);

  useEffect(() => {
    void loadChildren();
  }, [loadChildren]);

  async function runNow() {
    if (busy) return;
    setBusy(true);
    try {
      const res = await runInvoiceRecurringNow(invoice.id);
      // Three different things can have happened and they need three different
      // sentences. "Invoice made" over a self-posting series that failed to post
      // would let an operator walk away from an invoice still sitting here.
      toast(
        !res.autoSend
          ? t("sales.recurring.made", { number: res.childNumber })
          : res.autoSend.sent
            ? t("sales.recurring.madeAndSent", { number: res.childNumber })
            : t("sales.recurring.madeNotSent", {
              number: res.childNumber,
              reason: sendReason(t, res.autoSend.reason),
            }),
      );
      onChange();
      void loadChildren();
    } catch (err) {
      // The 409 carries the reason the generator declined, and each reason has a
      // different remedy: add lines, wait, reload. Flattening them all to
      // "something went wrong" turns a solvable state into a bug report.
      toast(errorLabel(t, err));
    } finally {
      setBusy(false);
    }
  }

  async function clear() {
    if (busy) return;
    setBusy(true);
    try {
      await setInvoiceRecurring(invoice.id, { everyMonths: null });
      onChange();
    } catch (err) {
      toast(errorLabel(t, err));
    } finally {
      setBusy(false);
    }
  }

  if (isChild) {
    // A generated invoice says where it came from and offers nothing else.
    return (
      <Card className="mt-4 px-[26px] py-[22px]">
        <div className="crm-card-t mb-2.5">{t("sales.recurring.title")}</div>
        <p className="crm-mut text-[13px] leading-relaxed">
          {t("sales.recurring.fromTemplate")}{" "}
          <Link to={`/sales/invoices/${invoice.recurringParentId}`} className="hover:underline">
            {t("sales.recurring.openTemplate")}
          </Link>
        </p>
      </Card>
    );
  }

  return (
    <Card className="mt-4 crm-list-stack">
      <CardHead
        title={t("sales.recurring.title")}
        sub={on ? scheduleLine(t, invoice) : t("sales.recurring.offSub")}
        actions={
          canManage ? (
            <span className="flex flex-wrap gap-2">
              {on && !finished ? (
                <Btn small onClick={runNow} disabled={busy}>
                  {busy ? <Loader2 className="size-4 animate-spin" /> : <Play className="size-4" />}
                  {t("sales.recurring.runNow")}
                </Btn>
              ) : null}
              <Btn small tone={on ? undefined : "dark"} onClick={() => setEditing(true)} disabled={busy}>
                <Repeat className="size-4" />
                {on ? t("sales.recurring.edit") : t("sales.recurring.turnOn")}
              </Btn>
              {on ? (
                <Btn small onClick={clear} disabled={busy}>
                  <X className="size-4" />
                  {t("sales.recurring.stop")}
                </Btn>
              ) : null}
            </span>
          ) : null
        }
      />

      {!on ? (
        <EmptyState
          icon={CalendarSync}
          title={t("sales.recurring.emptyTitle")}
          body={t("sales.recurring.emptyBody")}
        />
      ) : (
        <>
          <div className="crm-quick px-[26px] pb-1" style={{ "--crm-quick-cols": 3 } as CSSProperties}>
            <Fact
              label={t("sales.recurring.nextAt")}
              value={
                invoice.recurringNextAt ? (
                  fmtDate(invoice.recurringNextAt)
                ) : (
                  <span className="crm-mut">{t("sales.recurring.finished")}</span>
                )
              }
            />
            <Fact
              label={t("sales.recurring.countLabel")}
              value={
                invoice.recurringMaxCount != null
                  ? `${invoice.recurringCount} / ${invoice.recurringMaxCount}`
                  : String(invoice.recurringCount)
              }
            />
            {/* The honest one. A next date on its own cannot tell a healthy
                series from one whose machine stopped running months ago. */}
            <Fact
              label={t("sales.recurring.lastAt")}
              value={
                invoice.recurringLastAt ? (
                  fmtDate(invoice.recurringLastAt)
                ) : (
                  <span className="crm-mut">{t("sales.recurring.neverRun")}</span>
                )
              }
            />
          </div>

          {children.length > 0 ? (
            <div className="crm-rows">
              <div className="crm-list-head" style={{ gridTemplateColumns: COLS }}>
                <span>{t("sales.col.number")}</span>
                <span>{t("sales.col.date")}</span>
                <span>{t("sales.col.total")}</span>
              </div>
              {children.map((c) => (
                <div key={c.id} className="crm-list-row" style={{ gridTemplateColumns: COLS }}>
                  <span className="flex min-w-0 items-center gap-2.5">
                    <Link to={`/sales/invoices/${c.id}`} className="crm-prow-n crm-tnum crm-trunc">
                      {c.number}
                    </Link>
                    <StatusBadge status={c.status} />
                  </span>
                  <span className="crm-mut crm-tnum text-[12.5px]">{fmtDate(c.issueDate)}</span>
                  <span className="crm-tnum text-[13px] font-bold">
                    {formatCents(c.totalCents, c.currency)}
                  </span>
                </div>
              ))}
            </div>
          ) : null}
        </>
      )}

      {editing ? (
        <ScheduleModal
          invoice={invoice}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            onChange();
          }}
        />
      ) : null}
    </Card>
  );
}

function Fact({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="crm-q">
      <span className="min-w-0">
        <span className="crm-q-lab block">{label}</span>
        <span className="crm-q-val crm-trunc block crm-tnum">{value}</span>
      </span>
    </div>
  );
}

/** "Every month, until 31 December" - the schedule in one line, under the title. */
function scheduleLine(t: ReturnType<typeof useT>, inv: RecurringHost): string {
  const every = inv.recurringEveryMonths ?? 0;
  const period = every === 1 ? t("sales.recurring.everyMonth") : t("sales.recurring.everyN", { n: every });
  const limit = inv.recurringEndAt
    ? t("sales.recurring.until", { d: fmtDate(inv.recurringEndAt) })
    : inv.recurringMaxCount != null
      ? t("sales.recurring.times", { n: inv.recurringMaxCount })
      : null;
  // Self-posting is named on the card and not only inside the dialog. It is the
  // one setting here that can put a document in front of a client without
  // anybody pressing anything, so it should not be a thing you have to open a
  // form to find out about.
  const delivery = inv.recurringAutoSend ? t("sales.recurring.autoSendOn") : t("sales.recurring.autoSendOff");
  return [period, limit, delivery].filter(Boolean).join(" · ");
}

/**
 * The server's refusal as a fragment that fits inside the "not sent" sentence.
 *
 * Named reasons only. `no_recipient` and `disabled` are remedies an operator can
 * act on in a minute - add an address, switch the client's invoice mail back on -
 * and flattening them into "sending failed" turns both into a support ticket.
 */
function sendReason(t: ReturnType<typeof useT>, reason: string | null): string {
  switch (reason) {
    case "no_recipient": return t("sales.recurring.sendNoRecipient");
    case "disabled": return t("sales.recurring.sendDisabled");
    case "mark_failed": return t("sales.recurring.sendMarkFailed");
    default: return t("sales.recurring.sendFailed");
  }
}

/**
 * The schedule form.
 *
 * The whole schedule is sent every time, including the fields the operator did
 * not touch, because the endpoint is a PUT. An empty end date really means "no
 * end date" here, which is exactly the state a patch could not express.
 */
function ScheduleModal({
  invoice,
  onClose,
  onSaved,
}: {
  invoice: RecurringHost;
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useT();
  const { locale } = useI18n();
  const toast = useToast();

  const [every, setEvery] = useState(String(invoice.recurringEveryMonths ?? 1));
  // Blank on a new schedule: the API then starts it one period after the
  // invoice's own date, because THIS invoice is the first bill in the series.
  // Prefilling "today" would quietly charge a month that is already charged.
  const [nextAt, setNextAt] = useState(invoice.recurringNextAt ? toDateInput(invoice.recurringNextAt) : "");
  const [endAt, setEndAt] = useState(invoice.recurringEndAt ? toDateInput(invoice.recurringEndAt) : "");
  const [maxCount, setMaxCount] = useState(invoice.recurringMaxCount != null ? String(invoice.recurringMaxCount) : "");
  const [autoSend, setAutoSend] = useState(invoice.recurringAutoSend);
  const [busy, setBusy] = useState(false);

  const everyNum = Number(every);
  const maxNum = maxCount.trim() ? Number(maxCount) : null;
  // Mirrors the zod schema and the route's two extra rules. Not a substitute for
  // them - the server still refuses - but a disabled button beats a toast for a
  // mistake the form can see before it is made. `recurringCount` is the floor
  // because a limit at or below what the series already produced saves a
  // schedule that displays as live and never bills.
  const validEvery = Number.isInteger(everyNum) && everyNum >= 1 && everyNum <= 60;
  const validMax =
    maxNum === null ||
    (Number.isInteger(maxNum) && maxNum > invoice.recurringCount && maxNum <= 1000);
  const valid = validEvery && validMax;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!valid || busy) return;
    setBusy(true);
    try {
      await setInvoiceRecurring(invoice.id, {
        everyMonths: everyNum,
        // An empty picker is an explicit null, not an omission. That is the
        // difference this endpoint exists to express.
        //
        // `toScheduleIso` and not the shared `toIso`: that one builds local
        // midnight, which lands on the previous UTC day and would shift the
        // whole series back a day. See the note on `toScheduleIso`.
        nextAt: toScheduleIso(nextAt),
        endAt: toScheduleIso(endAt),
        maxCount: maxNum,
        // Always sent, never omitted. The endpoint reads a missing key as false,
        // so leaving it out here would switch self-posting off every time
        // somebody edited an end date.
        autoSend,
      });
      onSaved();
    } catch (err) {
      toast(errorLabel(t, err));
    } finally {
      setBusy(false);
    }
  }

  const title = invoice.recurringEveryMonths != null ? t("sales.recurring.edit") : t("sales.recurring.turnOn");

  return (
    <Modal title={title} label={title} onClose={onClose}>
      <form onSubmit={submit}>
        <label className="crm-lab">{t("sales.recurring.every")}</label>
        <label className="crm-inp">
          <input
            type="number"
            min={1}
            max={60}
            step={1}
            inputMode="numeric"
            value={every}
            onChange={(e) => setEvery(e.target.value)}
            className="crm-tnum"
            autoComplete="off"
          />
        </label>
        <div className="mt-2 flex flex-wrap gap-2">
          {PERIODS.map((p) => (
            <Btn key={p} small type="button" onClick={() => setEvery(String(p))}>
              {p === 1 ? t("sales.recurring.everyMonth") : t("sales.recurring.everyN", { n: p })}
            </Btn>
          ))}
        </div>

        <span className="crm-lab">{t("sales.recurring.nextAt")}</span>
        <DatePicker value={nextAt} onChange={setNextAt} locale={locale} placeholder={t("sales.recurring.nextAtAuto")} />
        <p className="crm-mut mt-1 text-[12px] leading-relaxed">{t("sales.recurring.nextAtHint")}</p>

        <span className="crm-lab">{t("sales.recurring.endAt")}</span>
        <DatePicker value={endAt} onChange={setEndAt} locale={locale} placeholder={t("sales.recurring.noEnd")} />

        <label className="crm-lab">{t("sales.recurring.maxCount")}</label>
        <label className="crm-inp">
          <input
            type="number"
            min={1}
            step={1}
            inputMode="numeric"
            value={maxCount}
            onChange={(e) => setMaxCount(e.target.value)}
            placeholder={t("sales.recurring.noLimit")}
            className="crm-tnum"
            autoComplete="off"
          />
        </label>
        {!validMax ? (
          <p className="mt-1 text-[12px] leading-relaxed text-red-600">
            {t("sales.recurring.errMaxBelow", { n: invoice.recurringCount })}
          </p>
        ) : null}

        <span className="crm-lab">{t("sales.recurring.delivery")}</span>
        <CheckField
          checked={autoSend}
          onChange={setAutoSend}
          label={
            <span className="flex items-center gap-2">
              <Send className="size-4" />
              {t("sales.recurring.autoSend")}
            </span>
          }
        />
        {/* The consequence spelled out under the box rather than left to the
            label. Ticking this is the moment a document starts reaching clients
            without anybody reading it first, and the sentence that says so
            belongs where the decision is made. */}
        <p className="crm-mut mt-1 text-[12px] leading-relaxed">
          {autoSend ? t("sales.recurring.autoSendHint") : t("sales.recurring.autoSendOffHint")}
        </p>

        <div className="crm-m-foot">
          <Btn type="button" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </Btn>
          <Btn type="submit" tone="dark" disabled={busy || !valid}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />}
            {t("common.save")}
          </Btn>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Turn the API's refusal into a sentence.
 *
 * The two rejections this form can actually provoke are worth naming: a generic
 * "something went wrong" on "end date before start date" sends the operator
 * looking for a bug instead of at the field they got wrong.
 */
function errorLabel(t: ReturnType<typeof useT>, err: unknown): string {
  const body = err instanceof ApiError ? (err.body as { error?: string; already?: number } | null) : null;
  switch (body?.error) {
    case "end_before_next": return t("sales.recurring.errEndBeforeNext");
    case "child_cannot_recur": return t("sales.recurring.errChild");
    case "next_in_past": return t("sales.recurring.errPast");
    case "next_already_billed": return t("sales.recurring.errAlreadyBilled");
    case "max_below_count": return t("sales.recurring.errMaxBelow", { n: body.already ?? 0 });
    // The generator's own refusals, which reach the screen through "issue next
    // now" rather than through the form.
    case "no_lines": return t("sales.recurring.errNoLines");
    case "not_due": return t("sales.recurring.errNotDue");
    case "raced": return t("sales.recurring.errRaced");
    case "not_recurring": return t("sales.recurring.errNotRecurring");
    default: return t("common.error");
  }
}
